import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingCart, ImageOff, Tag, ShieldCheck, Check } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { CheckoutConsents } from "@/components/CheckoutConsents";
import { LEGAL_VERSION } from "@/config/company";
import { productImage, formatPrice } from "@/lib/helpers";
import { metaTrack, META_FRIENDLY } from "@/lib/metaPixel";
import { toast } from "sonner";

const Cart = () => {
  const { t, lang } = useI18n();
  const { items, removeItem, updateQty, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const recoveryToken = params.get("recovery") || "";
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(false);
  const [immediate, setImmediate] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [guestEnabled, setGuestEnabled] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");

  useEffect(() => {
    api.get("/public-config").then(({ data }) => setGuestEnabled(!!data.guest_checkout)).catch(() => {});
  }, []);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(guestEmail.trim());

  useEffect(() => {
    if (!recoveryToken) return;
    if (!user) { toast.info(lang === "en" ? "Please log in to restore your cart." : "Accedi per ripristinare il carrello."); return; }
    api.get(`/recovery/resolve`, { params: { token: recoveryToken } })
      .then(({ data }) => {
        setRecovery(data);
        if (data.expired) toast.error(lang === "en" ? "This offer is no longer available." : "Questa offerta non è più disponibile.");
        else { toast.success(lang === "en" ? "Your cart has been restored." : "Il tuo carrello è stato ripristinato."); if (data.discount) toast.success(lang === "en" ? `Your ${data.discount.percent}% discount has been applied.` : `Il tuo sconto del ${data.discount.percent}% è stato applicato.`); }
      })
      .catch(() => toast.error(lang === "en" ? "Invalid recovery link." : "Link di recupero non valido."));
  }, [recoveryToken, user, lang]);

  const checkout = async () => {
    if (!user) {
      if (!guestEnabled) { toast.info(t("cart.loginRequired")); navigate("/login"); return; }
      if (!emailValid) { toast.error(lang === "en" ? "Enter a valid email" : "Inserisci un'email valida"); return; }
    }
    if (!terms || !immediate) { toast.error(t("consent.required")); return; }
    setLoading(true);
    // Meta Pixel InitiateCheckout: al click "Procedi al pagamento", PRIMA della sessione/redirect Stripe
    const contentIds = items.map((i) => i.sku || i.product_id);
    const contents = items.map((i) => ({ id: i.sku || i.product_id, quantity: i.quantity }));
    const contentName = items.length === 1
      ? (META_FRIENDLY[items[0].sku] || items[0].title_it || items[0].title_en)
      : "Carrello LearnSphereHub";
    metaTrack("InitiateCheckout", {
      value: Number(Number(total).toFixed(2)), currency: "EUR", content_type: "product",
      content_ids: contentIds, contents, content_name: contentName,
      num_items: items.reduce((s, i) => s + i.quantity, 0),
    });
    // lascia a fbq il tempo di inviare l'evento prima della navigazione a Stripe
    await new Promise((r) => setTimeout(r, 400));
    try {
      const payload = { items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })), origin_url: window.location.origin, terms_accepted: terms, immediate_supply_accepted: immediate, legal_version: LEGAL_VERSION, purchase_type: "digital_content" };
      let data;
      if (user) {
        const url = recoveryToken && recovery && !recovery.expired ? `/recovery/${recoveryToken}/checkout` : "/checkout";
        ({ data } = await api.post(url, payload));
      } else {
        const attemptId = (window.crypto?.randomUUID?.() || (String(Date.now()) + Math.random().toString(36).slice(2)));
        ({ data } = await api.post("/checkout/guest", { ...payload, email: guestEmail.trim().toLowerCase(), attempt_id: attemptId }));
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-brand-navy/5 flex items-center justify-center text-brand-navy/40"><ShoppingCart className="h-8 w-8" /></div>
        <h1 className="mt-6 text-2xl font-bold font-heading text-brand-navy">{t("cart.empty")}</h1>
        <Button onClick={() => navigate("/prodotti")} data-testid="cart-empty-cta" className="mt-6 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white">{t("cart.emptyCta")}</Button>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="text-3xl md:text-4xl font-extrabold font-heading text-brand-navy tracking-tight">{t("cart.title")}</h1>
      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => {
            const img = productImage(i, 200);
            const title = lang === "en" ? (i.title_en || i.title_it) : (i.title_it || i.title_en);
            return (
              <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 rounded-2xl border border-brand-navy/10 bg-white p-4">
                <div className="h-20 w-20 rounded-xl overflow-hidden bg-brand-light shrink-0">
                  {img ? <img src={img} alt={title} className="w-full h-full object-contain p-1" /> : <div className="w-full h-full flex items-center justify-center text-brand-navy/30"><ImageOff className="h-6 w-6" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-brand-navy truncate">{title}</h3>
                  <p className="text-sm text-brand-navy/50">{formatPrice(i.price)}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <button data-testid={`qty-dec-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity - 1)} className="h-7 w-7 rounded-md border border-brand-navy/15 flex items-center justify-center text-brand-navy hover:border-brand-electric"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-8 text-center text-sm font-medium" data-testid={`qty-${i.product_id}`}>{i.quantity}</span>
                    <button data-testid={`qty-inc-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity + 1)} className="h-7 w-7 rounded-md border border-brand-navy/15 flex items-center justify-center text-brand-navy hover:border-brand-electric"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <span className="font-heading font-bold text-brand-navy">{formatPrice(i.price * i.quantity)}</span>
                  <button data-testid={`remove-${i.product_id}`} onClick={() => removeItem(i.product_id)} className="text-brand-navy/40 hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-brand-navy/10 bg-white p-6 sticky top-24">
            <div className="flex items-center justify-between text-lg">
              <span className="font-heading font-semibold text-brand-navy">{t("cart.total")}</span>
              <span data-testid="cart-total" className="font-heading font-bold text-brand-navy">{formatPrice(total)}</span>
            </div>
            {recovery && recovery.discount && !recovery.expired && (
              <div data-testid="recovery-summary" className="mt-3 rounded-xl border border-brand-electric/30 bg-brand-electric/5 p-3 text-sm">
                <div className="flex items-center gap-2 font-semibold text-brand-navy"><Tag className="h-4 w-4 text-brand-electric" /> {lang === "en" ? "Recovery discount" : "Sconto recupero"} {recovery.discount.percent}%</div>
                <div className="mt-2 flex justify-between text-brand-navy/70"><span>{lang === "en" ? "Products total" : "Totale prodotti"}</span><span>{formatPrice(recovery.original_total)}</span></div>
                <div className="flex justify-between text-brand-electric"><span>{lang === "en" ? "Discount" : "Sconto"} {recovery.discount.percent}%</span><span>-{formatPrice(recovery.discount.amount)}</span></div>
                <div className="mt-1 flex justify-between font-bold text-brand-navy border-t border-brand-navy/10 pt-1"><span>{lang === "en" ? "Total to pay" : "Totale da pagare"}</span><span data-testid="recovery-total">{formatPrice(recovery.discount.discounted_total)}</span></div>
                {recovery.discount.expires_at_human && <p className="mt-2 text-xs text-brand-navy/50">{lang === "en" ? "Offer valid until" : "Offerta valida fino al"} {recovery.discount.expires_at_human}</p>}
              </div>
            )}
            {!user && !guestEnabled && <p className="mt-3 text-xs text-brand-orange">{t("cart.loginRequired")}</p>}
            {!user && guestEnabled && (
              <div data-testid="guest-checkout-block" className="mt-3 rounded-xl border border-brand-navy/10 bg-white p-3">
                <label htmlFor="guest-email" className="text-sm font-semibold text-brand-navy">
                  {lang === "en" ? "Checkout as guest" : "Acquista come ospite"}
                </label>
                <p className="mt-0.5 text-xs text-brand-navy/60">
                  {lang === "en"
                    ? "Enter your email: we'll send the secure download link there after payment. No account required."
                    : "Inserisci la tua email: dopo il pagamento ti invieremo lì il link sicuro di download. Nessun account richiesto."}
                </p>
                <Input id="guest-email" data-testid="guest-email-input" type="email" value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)} placeholder={lang === "en" ? "you@email.com" : "tua@email.com"}
                  className="mt-2 h-10" />
                <p className="mt-2 text-xs text-brand-navy/50">
                  {lang === "en" ? "Prefer an account? " : "Preferisci un account? "}
                  <Link to="/login" className="text-brand-electric hover:underline">{lang === "en" ? "Log in" : "Accedi"}</Link>
                </p>
              </div>
            )}

            <div data-testid="cart-trust-badges" className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-brand-navy/60">
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand-electric" /> {lang === "en" ? "Secure payment" : "Pagamento sicuro"}</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand-electric" /> {lang === "en" ? "Digital product" : "Prodotto digitale"}</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-brand-electric" /> {lang === "en" ? "LearnSphereHub support" : "Assistenza LearnSphereHub"}</span>
            </div>

            <div data-testid="cart-secure-checkout" className="mt-4 rounded-xl border border-brand-navy/10 bg-brand-light/60 p-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-brand-navy">
                <ShieldCheck className="h-4 w-4 text-brand-electric" /> {lang === "en" ? "Protected purchase" : "Acquisto protetto"}
              </p>
              <p className="mt-1 text-xs text-brand-navy/60 leading-relaxed">
                {lang === "en"
                  ? "Payment is processed through Stripe. After payment confirmation, the digital product will be available following the normal LearnSphereHub procedure. Support is available if you have any issue with your purchase."
                  : "Pagamento elaborato tramite Stripe. Dopo la conferma del pagamento il prodotto digitale sarà disponibile secondo il normale sistema LearnSphereHub. Assistenza disponibile in caso di problemi con l'acquisto."}
              </p>
            </div>

            <CheckoutConsents variant="download" terms={terms} setTerms={setTerms} immediate={immediate} setImmediate={setImmediate} idPrefix="cart" />
            <Button data-testid="checkout-btn" onClick={checkout} disabled={loading || !terms || !immediate || (!user && guestEnabled && !emailValid)}
              className="mt-5 w-full rounded-full h-12 bg-brand-electric hover:bg-brand-electric/90 text-white">
              {loading ? t("common.loading") : t("cart.checkout")}
            </Button>
            <p data-testid="checkout-secure-microcopy" className="mt-2 flex items-center justify-center gap-1.5 text-xs text-brand-navy/50">
              <ShieldCheck className="h-3.5 w-3.5" /> {lang === "en" ? "Secure payment via Stripe" : "Pagamento sicuro tramite Stripe"}
            </p>
            <Link to="/prodotti" className="mt-3 block text-center text-sm text-brand-navy/50 hover:text-brand-electric">{t("cart.emptyCta")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
